import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Check, Crown, Sparkles, MessageCircle, ScanLine, Loader2, Gift, Zap } from "lucide-react";
import MobileShell from "@/components/MobileShell";
import client, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { track } from "@/lib/analytics";

const CURRENCIES = [
  { code: "eur", label: "EUR", symbol: "€" },
];

const FEATURE_KEYS = [
  { icon: Sparkles, key: "up_feat_langs" },
  { icon: MessageCircle, key: "up_feat_stories" },
  { icon: ScanLine, key: "up_feat_lessons" },
  { icon: Zap, key: "up_feat_ai" },
];

const fmt = (n) => (n ?? 0).toLocaleString("de-CH");

export default function Upgrade() {
  const nav = useNavigate();
  const { premium, subscription, refreshSubscription } = useAuth();
  const { t } = useLang();
  const [plans, setPlans] = useState([]);
  const [currency] = useState("eur");
  const [interval, setInterval] = useState("month");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { client.get("/plans").then(({ data }) => setPlans(data.plans || [])).catch(() => {}); }, []);
  useEffect(() => { track("pro_view"); }, []);

  const lookupKey = `premium_${interval === "year" ? "yearly" : "monthly"}_${currency}`;
  const plan = plans.find((p) => p.lookup_key === lookupKey);
  const monthlyPlan = plans.find((p) => p.lookup_key === `premium_monthly_${currency}`);
  const yearlyPlan = plans.find((p) => p.lookup_key === `premium_yearly_${currency}`);
  const price = plan ? plan.amount : (interval === "year" ? 149.90 : 14.90);
  const symbol = CURRENCIES.find((c) => c.code === currency)?.symbol || "";
  const savePct = monthlyPlan && yearlyPlan ? Math.round((1 - yearlyPlan.amount / (monthlyPlan.amount * 12)) * 100) : 16;

  const subscribe = async () => {
    setLoading(true);
    try {
      const { data } = await client.post("/payments/checkout", { lookup_key: lookupKey, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setLoading(false);
    }
  };

  const cancel = async () => {
    setBusy(true);
    try { await client.post("/subscription/cancel"); await refreshSubscription(); toast.success(t("sub_cancel_toast")); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const resume = async () => {
    setBusy(true);
    try { await client.post("/subscription/resume"); await refreshSubscription(); toast.success(t("sub_resumed_toast")); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const portal = async () => {
    setBusy(true);
    try {
      const { data } = await client.post("/subscription/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };

  return (
    <MobileShell>
      <div className="relative px-5 py-6 min-h-screen">
        <div className="absolute -top-10 left-1/2 -translate-x-1/2 w-72 h-72 rounded-full bg-blue-600/18 blur-3xl pointer-events-none" />
        <header className="flex items-center gap-3 mb-6 relative z-10">
          <button onClick={() => nav(-1)} data-testid="upgrade-back" className="w-10 h-10 rounded-full glass flex items-center justify-center text-slate-300 press"><ArrowLeft size={20} /></button>
          <h1 className="font-heading text-2xl font-extrabold text-white">MELLETIC Premium</h1>
        </header>

        <div className="relative z-10 flex flex-col items-center text-center mb-6">
          <div className="relative mb-1">
            <div className="w-[84px] h-[84px] rounded-[26px] mel-gradient flex items-center justify-center shadow-[0_22px_54px_-14px_rgba(37,99,235,0.8)] ring-1 ring-white/40" data-testid="premium-badge">
              <div style={{
                height: 40, width: 40 * (490 / 243),
                WebkitMaskImage: "url(/melletic-mark.png)", maskImage: "url(/melletic-mark.png)",
                WebkitMaskSize: "contain", maskSize: "contain",
                WebkitMaskRepeat: "no-repeat", maskRepeat: "no-repeat",
                WebkitMaskPosition: "center", maskPosition: "center",
                background: "#ffffff",
                filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.18))",
              }} className="select-none block" />
            </div>
            <span className="absolute -top-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center shadow-[0_6px_16px_-4px_rgba(217,119,6,0.8)] ring-[3px] ring-white"
              style={{ background: "linear-gradient(135deg,#fcd34d 0%,#f59e0b 60%,#d97706 100%)" }}>
              <Crown size={16} className="text-white" fill="currentColor" />
            </span>
          </div>
          <h2 className="font-heading text-xl font-extrabold text-white mt-4">MELLETIC PRO</h2>
          <p className="text-slate-400 text-sm mt-1 max-w-[300px]">{t("up_pro_sub")}</p>
        </div>

        {premium ? (
          <div className="relative z-10 glass-strong rounded-3xl p-6" data-testid="premium-active-card">
            <div className="flex items-center gap-2 text-emerald-300 font-bold">
              <Check size={18} /> {subscription?.status === "trialing" ? t("premium_trial_period") : t("premium_active")}
            </div>
            <p className="text-slate-300 text-sm mt-2">
              {t("plan_word")}: <b className="text-white">{(subscription?.currency || "").toUpperCase()} {subscription?.amount ?? 10}{subscription?.interval === "year" ? "/" + t("per_year") : subscription?.interval === "manual" ? "" : "/" + t("per_month")}</b>
            </p>
            {subscription?.trial_end && subscription?.status === "trialing" && (
              <p className="text-amber-300 text-xs mt-1">{t("free_until")}: {new Date(subscription.trial_end).toLocaleDateString("de-CH")}</p>
            )}
            {subscription?.current_period_end && (
              <p className="text-slate-400 text-xs mt-1">
                {subscription?.cancel_at_period_end ? t("expires_word") : t("renews_word")}: {new Date(subscription.current_period_end).toLocaleDateString("de-CH")}
              </p>
            )}
            {subscription?.manual && <p className="text-slate-500 text-xs mt-1">{t("manual_access")}</p>}
            {!subscription?.manual && subscription?.stripe_subscription_id && (
              subscription?.cancel_at_period_end ? (
                <button onClick={resume} disabled={busy} data-testid="resume-btn" className="w-full mel-gradient text-white font-semibold py-3 rounded-2xl mt-4 press disabled:opacity-60">
                  {busy ? <Loader2 className="animate-spin mx-auto" size={18} /> : t("resume_sub")}
                </button>
              ) : (
                <button onClick={cancel} disabled={busy} data-testid="cancel-btn" className="w-full glass text-red-300 font-semibold py-3 rounded-2xl mt-4 press disabled:opacity-60">
                  {busy ? <Loader2 className="animate-spin mx-auto" size={18} /> : t("cancel_sub")}
                </button>
              )
            )}
            {!subscription?.manual && subscription?.stripe_customer_id && (
              <button onClick={portal} disabled={busy} data-testid="manage-billing-btn" className="w-full glass text-slate-200 font-semibold py-3 rounded-2xl mt-3 press disabled:opacity-60">
                {t("manage_billing") || "Manage billing"}
              </button>
            )}
          </div>
        ) : (
          <div className="relative z-10">
            <div className="glass-strong rounded-3xl p-6 border border-blue-400/30">
              {/* interval toggle */}
              <div className="flex gap-2 mb-3" data-testid="interval-toggle">
                <button onClick={() => setInterval("month")} data-testid="interval-month" className={`flex-1 py-2 rounded-xl font-bold text-sm transition-all press ${interval === "month" ? "mel-gradient text-white" : "glass text-slate-300"}`}>{t("monthly")}</button>
                <button onClick={() => setInterval("year")} data-testid="interval-year" className={`relative flex-1 py-2 rounded-xl font-bold text-sm transition-all press ${interval === "year" ? "mel-gradient text-white" : "glass text-slate-300"}`}>
                  {t("yearly")}
                  <span className="absolute -top-2 -right-1 text-[9px] font-extrabold px-1.5 py-0.5 rounded-full bg-emerald-400 text-emerald-950">-{savePct}%</span>
                </button>
              </div>
              <div className="flex items-end justify-center gap-1 mb-1 mt-5">
                <span className="font-heading text-5xl font-extrabold text-white">{symbol} {Number(price).toFixed(2)}</span>
                <span className="text-slate-400 mb-1.5">/ {interval === "year" ? t("up_per_year") : t("up_per_month")}</span>
              </div>
              {interval === "year" && (
                <p className="text-center text-emerald-300 text-xs font-semibold mb-1" data-testid="annual-note">{t("up_annual_note", { p: savePct })}</p>
              )}
              <div className="flex items-center justify-center gap-1.5 text-amber-300 text-sm font-semibold mb-5" data-testid="trial-note">
                <Check size={15} /> {t("cancel_anytime")}
              </div>
              <ul className="space-y-3 mb-6">
                {FEATURE_KEYS.map((f, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-200">
                    <span className="w-8 h-8 rounded-lg bg-blue-500/12 text-blue-600 flex items-center justify-center shrink-0"><f.icon size={17} /></span>
                    <span className="text-sm font-medium">{t(f.key)}</span>
                  </li>
                ))}
              </ul>
              <button onClick={subscribe} disabled={loading} data-testid="subscribe-btn"
                className="w-full mel-gradient text-white font-bold py-4 rounded-2xl press shadow-[0_14px_34px_-14px_rgba(37,99,235,0.9)] disabled:opacity-60 flex items-center justify-center gap-2">
                {loading ? <><Loader2 className="animate-spin" size={18} /> {t("opening_word")}</> : <>{t("start_trial")}</>}
              </button>
              <p className="text-center text-xs text-slate-500 mt-3">{t("subscribe_note")}</p>
            </div>
          </div>
        )}
      </div>
    </MobileShell>
  );
}
